import { useEffect, useState } from 'react';

import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import Modal from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';
import { getZendeskUrl } from '@/utils/integration';
import { generateRandomString } from '@/utils/util';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import { IMPORT_STATE, type ImportRuleT, type ZendeskInfo } from '../types';
import { useZendesk } from './useZendesk';
import { ZendeskImportTemplate } from './zendesk-import-template';
import ZendeskSettingModal from './ZendeskSettingModal';
import ZendeskSubdomailModal from './ZendeskSubdomailModal';

export const ZendeskItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const {
    zendeskWorkspace,
    disconnectZendeskMutation,
    updateZendeskImportRuleMutation,
    requestImportMutation,
    setZendeskWorkspace,
  } = useZendesk(!isConnector);

  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<ZendeskInfo | null>('zendesk-redirect', null);

  const [importModalState, setImportModalState] = useState<{
    isNew: boolean;
  } | null>(null);
  const [settingModalState, setSettingModalState] = useState(false);
  const [subDomainModalState, setSubDomainModalState] = useState(false);

  const handleConnect = () => {
    setSubDomainModalState(true);
  };

  const handleConnectWithSubdomain = (subdomain: string) => {
    if (!currWorkspace) return;
    const state = generateRandomString();

    sessionStorage.setItem(
      state,
      JSON.stringify({ workspaceAlias: currWorkspace.alias, subdomain }),
    );

    window.open(getZendeskUrl(subdomain, state));
  };

  const handleDisconnect = () => {
    disconnectZendeskMutation();
  };

  const handleSaveImportRule = async (importRule: ImportRuleT) => {
    await updateZendeskImportRuleMutation(importRule);
  };

  const handleImport = async (
    start_date: string,
    end_date: string,
    importRule: ImportRuleT,
  ) => {
    await requestImportMutation({
      start_date,
      end_date,
      importRule,
    });
  };

  useEffect(() => {
    if (isConnector) return;
    if (!currWorkspace) return;
    if (!redirect) return;

    setRedirect(null);
    removeKey();
    setZendeskWorkspace(redirect);
    fetchFeedbackSources(currWorkspace.alias);
    setImportModalState({ isNew: true });
  }, [
    currWorkspace,
    fetchFeedbackSources,
    isConnector,
    redirect,
    removeKey,
    setRedirect,
    setZendeskWorkspace,
  ]);

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!zendeskWorkspace}
        source={SOURCE_TYPE.ZENDESK}
        description="Import userchats as feedback into Syncly."
        connectedDisplay={zendeskWorkspace?.domain || 'Zendesk'}
        info={zendeskWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={handleConnect}
        additionalActions={(workspace) =>
          workspace.import_state === IMPORT_STATE.IMPORTING ? (
            <div className="flex items-center gap-2">
              <Spinner />
              <Typo type="body2" className="text-primary">
                Importing
              </Typo>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outlined" onClick={() => setSettingModalState(true)}>
                Trigger option
              </Button>
              <Button
                variant="outlined"
                onClick={() => setImportModalState({ isNew: false })}
              >
                One-time import
              </Button>
            </div>
          )
        }
      />

      <Modal
        status={!!importModalState}
        closeModal={() => setImportModalState(null)}
        useBackgroundClose
      >
        <IntegrationImportModal
          isNew={importModalState?.isNew}
          title={'Import Tickets'}
          subTitle={'Zendesk'}
          subTitleList={zendeskWorkspace?.domain}
          historyList={zendeskWorkspace?.history || []}
          importRule={zendeskWorkspace?.import_rule}
          template={ZendeskImportTemplate}
          onSaveWithoutImport={handleSaveImportRule}
          onSubmitImport={handleImport}
        />
      </Modal>

      <Modal
        status={settingModalState}
        closeModal={() => setSettingModalState(false)}
        useBackgroundClose
      >
        <ZendeskSettingModal
          importRule={zendeskWorkspace?.import_rule}
          onSave={handleSaveImportRule}
        />
      </Modal>

      <Modal
        status={subDomainModalState}
        closeModal={() => setSubDomainModalState(false)}
        useBackgroundClose
      >
        <ZendeskSubdomailModal onConnect={handleConnectWithSubdomain} />
      </Modal>
    </>
  );
};
